import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsIcon from '@mui/icons-material/Directions';
import {
  listMyBookings, cancelMyBooking, startAssessment, completeAssessment,
} from '../services/internApi';
import { formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';
import ConfirmDialog from '../components/common/ConfirmDialog';

const AUTO_REFRESH_MS = 30000;

function buildAddressString(addr) {
  if (!addr) return '';
  return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
}

function buildMapsUrl(addr) {
  const s = buildAddressString(addr);
  return s ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s)}` : null;
}

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelBookingItem, setCancelBookingItem] = useState(null);
  const [startBookingItem, setStartBookingItem] = useState(null);
  const [completeBookingItem, setCompleteBookingItem] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completeConfirmed, setCompleteConfirmed] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const intervalRef = useRef(null);

  const loadBookings = useCallback((f) => {
    setLoading(true);
    return listMyBookings(f)
      .then((data) => setBookings(data.bookings || []))
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load bookings'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadBookings(filter);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      // Silent refresh in background
      listMyBookings(filter)
        .then((data) => setBookings(data.bookings || []))
        .catch(() => {});
    }, AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [filter, loadBookings]);

  const handleCancel = async () => {
    if (!cancelBookingItem) return;
    setActionLoading(true);
    try {
      await cancelMyBooking(cancelBookingItem.booking_id, 'Intern cancelled');
      setSnack({ open: true, message: 'Booking released. Another intern can now pick it up.', severity: 'success' });
      setCancelBookingItem(null);
      loadBookings(filter);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to cancel', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStart = async () => {
    if (!startBookingItem) return;
    setActionLoading(true);
    try {
      await startAssessment(startBookingItem.booking_id);
      const id = startBookingItem.booking_id;
      setStartBookingItem(null);
      navigate(`/bookings/${id}/assess`);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to start', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completeBookingItem) return;
    setActionLoading(true);
    try {
      await completeAssessment(completeBookingItem.booking_id, completionNotes);
      setSnack({
        open: true,
        message: 'Assessment marked complete. Upload your report next.',
        severity: 'success',
      });
      setCompleteBookingItem(null);
      setCompletionNotes('');
      setCompleteConfirmed(false);
      loadBookings(filter);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.error || 'Failed to mark complete', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>My Bookings</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Assessments you've accepted. Auto-refreshes every 30 seconds.
      </Typography>

      <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ mb: 2 }}>
        <Tab value="upcoming" label="Upcoming" />
        <Tab value="in_review" label="In Review" />
        <Tab value="past" label="Past" />
        <Tab value="cancelled" label="Cancelled" />
        <Tab value="all" label="All" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No bookings in this view</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {bookings.map((b) => {
            const mapsUrl = buildMapsUrl(b.client_address);
            const isUpcoming = b.status === 'confirmed' && b.time_slot > new Date().toISOString();
            const hoursUntilSlot = b.time_slot
              ? (new Date(b.time_slot).getTime() - Date.now()) / 3600000
              : null;
            const withinStartWindow = hoursUntilSlot !== null && hoursUntilSlot <= 1;
            const canStart = b.status === 'confirmed' && withinStartWindow;
            const startTooltip = b.status !== 'confirmed'
              ? ''
              : hoursUntilSlot > 1
                ? `Available within 1 hour of scheduled time (${hoursUntilSlot.toFixed(1)}h to go)`
                : '';
            const minsSinceSlot = hoursUntilSlot !== null ? -hoursUntilSlot * 60 : null;
            const pastCompleteThreshold = minsSinceSlot !== null && minsSinceSlot >= 30;
            const showComplete = b.status === 'confirmed' || b.status === 'in_progress';
            const canComplete = showComplete && pastCompleteThreshold;
            const completeTooltip = !showComplete
              ? ''
              : !pastCompleteThreshold
                ? minsSinceSlot < 0
                  ? `Available 30 min after scheduled start (${Math.abs(minsSinceSlot).toFixed(0)} min until start)`
                  : `Available ${(30 - minsSinceSlot).toFixed(0)} min from now (30 min after scheduled start)`
                : '';
            const isActive = ['confirmed', 'in_progress', 'pending_report', 'needs_revision'].includes(b.status);
            return (
              <Grid item xs={12} md={6} key={b.booking_id}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {b.service_type === 'business' ? 'Business Assessment' : 'Home Assessment'}
                    </Typography>
                    <StatusChip status={b.status} type="booking" />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2">{formatDate(b.time_slot)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">{b.client_name}</Typography>
                    </Box>
                    {b.client_phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          <a href={`tel:${b.client_phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                            {b.client_phone}
                          </a>
                        </Typography>
                      </Box>
                    )}
                    {b.client_address && Object.keys(b.client_address || {}).length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationOnIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">{buildAddressString(b.client_address)}</Typography>
                          {mapsUrl && (
                            <Button
                              size="small"
                              startIcon={<DirectionsIcon />}
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ mt: 0.5, p: 0, justifyContent: 'flex-start' }}
                            >
                              Get directions
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {b.status === 'pending_report' && (
                    <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Assessment submitted{b.submitted_at ? ` · ${formatDate(
                          typeof b.submitted_at === 'number' || /^\d+$/.test(String(b.submitted_at))
                            ? new Date(Number(b.submitted_at) * 1000).toISOString()
                            : b.submitted_at
                        )}` : ''}
                      </Typography>
                      <Typography variant="body2">
                        A mentor is reviewing your work. You&apos;ll get an email when the report is delivered to the client, or if a revision is requested. No action needed from you right now.
                      </Typography>
                    </Alert>
                  )}

                  {b.status === 'needs_revision' && (
                    <Alert severity="warning" sx={{ mb: 1, alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Mentor requested revisions
                      </Typography>
                      {b.mentor_feedback ? (
                        <Box
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.6)',
                            borderRadius: 1,
                            p: 1.5,
                            mt: 1,
                            mb: 1,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'inherit',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            border: '1px solid rgba(237,108,2,0.25)',
                          }}
                        >
                          {b.mentor_feedback}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                          (Feedback not available on this record — check your email.)
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => navigate(`/bookings/${b.booking_id}/assess`)}
                        sx={{ mt: 1 }}
                      >
                        Open to Revise
                      </Button>
                    </Alert>
                  )}

                  {isActive && b.status !== 'pending_report' && b.status !== 'needs_revision' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {b.status === 'confirmed' && (
                        <Tooltip title={startTooltip} disableHoverListener={!startTooltip}>
                          <span>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => setStartBookingItem(b)}
                              disabled={!canStart}
                              fullWidth
                            >
                              Start Assessment
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {b.status === 'in_progress' && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => navigate(`/bookings/${b.booking_id}/assess`)}
                          fullWidth
                        >
                          Resume Assessment
                        </Button>
                      )}
                      {isUpcoming && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => setCancelBookingItem(b)}
                          size="small"
                          fullWidth
                        >
                          Release Booking
                        </Button>
                      )}
                      {showComplete && (
                        <Tooltip title={completeTooltip} disableHoverListener={!completeTooltip}>
                          <span>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => setCompleteBookingItem(b)}
                              disabled={!canComplete}
                              fullWidth
                            >
                              Mark Complete
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Start Assessment Dialog */}
      <ConfirmDialog
        open={!!startBookingItem}
        title="Start this assessment?"
        message="Confirm you've arrived on-site and are ready to begin. Only do this when you're at the client's location and they're ready."
        confirmText="Yes, start now"
        confirmColor="primary"
        onConfirm={handleStart}
        onCancel={() => setStartBookingItem(null)}
        loading={actionLoading}
      />

      {/* Complete Assessment Dialog with Notes */}
      <Dialog open={!!completeBookingItem} onClose={() => { setCompleteBookingItem(null); setCompleteConfirmed(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Assessment Complete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This cannot be undone from the intern portal. Only use this when the on-site assessment is actually finished — not as a way to release a booking. To release, click "Cancel" below and use the red "Release Booking" button instead.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            After this you'll need to upload your assessment report (you'll get a separate notification with instructions).
          </Typography>
          <TextField
            label="Notes (optional)"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Anything notable about the assessment, client interaction, or findings..."
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={completeConfirmed}
                onChange={(e) => setCompleteConfirmed(e.target.checked)}
              />
            }
            label="I confirm the on-site assessment is finished and I'm ready to upload my report."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCompleteBookingItem(null); setCompletionNotes(''); setCompleteConfirmed(false); }}>Cancel</Button>
          <Button onClick={handleComplete} variant="contained" color="success" disabled={actionLoading || !completeConfirmed}>
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'Mark Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <ConfirmDialog
        open={!!cancelBookingItem}
        title="Release this booking?"
        message="The booking will return to pending status so another intern can accept it. Use this only if you genuinely can't make it. Frequent cancellations may affect your standing."
        confirmText="Yes, release"
        confirmColor="error"
        onConfirm={handleCancel}
        onCancel={() => setCancelBookingItem(null)}
        loading={actionLoading}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
